import { useState, useEffect } from 'react';
import { Fingerprint, Delete, ShieldCheck, Hexagon } from 'lucide-react';
import { guard, biometricAvailable, loginBiometric, passkeyStatus } from '../lib/crypto';
import { vibrate } from '../lib/bus';
import { devApi, devToken } from '../lib/api';
import { useBrand } from './BrandProvider';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'];

function Dots({ value, error }) {
  return (
    <div data-testid="pin-dots" className={`flex gap-3 justify-center my-8 ${error ? 'shake-x' : ''}`}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className={`h-3.5 w-3.5 rounded-full border transition-colors duration-200 ${i < value.length ? 'bg-cyan-400 border-cyan-300 shadow-[0_0_14px_rgb(var(--cyan-500))]' : 'border-violet-500/50'}`} />
      ))}
    </div>
  );
}

export default function TitanGuard({ onUnlock }) {
  const { brand } = useBrand();
  const [mode, setMode] = useState(null);
  const [pin, setPin] = useState('');
  const [first, setFirst] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bio, setBio] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    biometricAvailable().then(setBio);
    passkeyStatus().then(setHasPasskey);
    devApi.status().then((s) => setMode(s.registered ? 'locked' : 'setup'))
      .catch(() => { setOffline(true); setMode(guard.get() ? 'locked' : 'setup'); });
  }, []);

  const finish = (token) => {
    devToken.set(token);
    vibrate(15); onUnlock(token);
  };
  const tryBio = async () => {
    setBusy(true);
    try { finish(await loginBiometric()); }
    catch (e) { fail(e.status ? e.message : 'Face ID / Impronta annullati: riprova o usa il PIN'); }
    finally { setBusy(false); }
  };

  const submit = async (p) => {
    setBusy(true);
    try {
      if (mode === 'setup') {
        if (!first) { setFirst(p); setPin(''); return; }
        if (p !== first) { setFirst(null); return fail('I PIN non coincidono'); }
        const r = offline ? { token: null } : await devApi.register(p);
        await guard.sync(p);
        finish(r.token);
      } else if (offline) {
        const r = await guard.verify(p);
        if (r === true) finish(null); else fail(r === 'locked' ? 'Troppi tentativi: attendi 30s' : 'PIN errato');
      } else {
        const r = await devApi.login(p);
        await guard.sync(p);
        finish(r.token);
      }
    } catch (e) {
      if (e.status) fail(e.message);
      else { setOffline(true); fail('Server non raggiungibile: modalità offline'); }
    } finally { setBusy(false); }
  };
  const fail = (msg) => { setError(msg); setPin(''); vibrate([40, 50, 40]); setTimeout(() => setError(''), 2200); };

  const press = (k) => {
    if (busy || !mode || !k) return;
    vibrate(8);
    if (k === 'del') return setPin((p) => p.slice(0, -1));
    const n = (pin + k).slice(0, 6);
    setPin(n);
    if (n.length === 6) setTimeout(() => submit(n), 120);
  };

  useEffect(() => {
    const h = (e) => { if (/^\d$/.test(e.key)) press(e.key); if (e.key === 'Backspace') press('del'); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  const title = !mode ? 'Connessione…' : mode === 'setup' ? (first ? 'Conferma il PIN' : 'Crea il PIN sviluppatore') : 'Area Sviluppatore';
  const showBio = mode === 'locked' && !offline && bio && hasPasskey;
  return (
    <div data-testid="titan-guard" className="titan-root titan-grid min-h-screen flex items-center justify-center px-6 pt-safe pb-safe">
      <div className="relative z-10 w-full max-w-sm rise">
        <div className="flex items-center gap-3 mb-10">
          <Hexagon className="h-9 w-9 text-violet-400" strokeWidth={1.5} />
          <div>
            <div className="text-xl font-extrabold tracking-[.25em] neon-text">{brand.name.toUpperCase()}</div>
            <div className="t-label">{brand.tagline} // area riservata</div>
          </div>
        </div>
        <div className="glass glass-edge rounded-2xl p-7 relative overflow-hidden scanline">
          <div className="flex items-center gap-2 text-cyan-300 text-sm"><ShieldCheck className="h-4 w-4" /><span data-testid="guard-title">{title}</span></div>
          <p className="text-xs text-slate-400 mt-2 t-chat">
            {mode === 'setup' ? 'Primo avvio: scegli 6 cifre. Il server ne salva solo l\'hash bcrypt: sarà l\'unico PIN in grado di pubblicare nello Store.' : (showBio ? 'Entra con Face ID / Impronta oppure con il PIN segreto, entrambi verificati dal server.' : 'Inserisci il PIN segreto verificato dal server. Puoi attivare Face ID / Impronta dalle impostazioni di sicurezza.')}
          </p>
          {offline && <div data-testid="guard-offline" className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-200">Offline: accesso locale, IA e pubblicazione disattivate</div>}
          {showBio && (
            <div className="pt-6 flex flex-col items-center gap-2">
              <button data-testid="guard-bio-btn" onClick={tryBio} disabled={busy} className="h-20 w-20 rounded-full key-holo flex items-center justify-center neon-pulse disabled:opacity-40" aria-label="Entra con Face ID o Impronta">
                <Fingerprint className="h-10 w-10 text-violet-300" />
              </button>
              <span className="text-[11px] text-slate-400">Face ID / Impronta · oppure digita il PIN</span>
            </div>
          )}
          <Dots value={pin} error={!!error} />
          <div data-testid="guard-error" className="h-5 text-center text-xs text-rose-400">{error}</div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {KEYS.map((k, i) => (
              <button key={i} data-testid={`pin-key-${k || 'empty'}`} onClick={() => press(k)} disabled={!k || !mode || busy}
                className={`key-holo h-16 rounded-xl text-2xl font-semibold text-slate-100 flex items-center justify-center disabled:opacity-20 ${!k ? 'invisible' : ''}`}>
                {k === 'del' ? <Delete className="h-5 w-5" /> : k}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="guard-back-store" onClick={() => { window.location.href = '/'; }} className="block mx-auto mt-6 text-xs text-cyan-300 hover:text-cyan-200">← Torna allo Store</button>
        <p className="t-label text-center mt-4">Auto-lock dopo inattività · WebAuthn {bio ? 'disponibile' : 'non disponibile'}</p>
      </div>
    </div>
  );
}
